import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { getToken } from '../../../features/token/tokenSaver';
import { getAdvertiserAccount } from '../../../services/getAdvertiserAccount';
import { activateAdvertiserAccount } from '../../../services/activateAdvertiserAccount';
import { usePlatformAdvertisingAgreement } from '../../../hooks/usePlatformAdvertisingAgreement';
import './AdvertiserActivate.css';

import { platformAgreementPath } from '../../../utils/platformAgreementUrl';

const currencyFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
});

function ActivateNav({ onLogoClick }) {
    return (
        <nav className="advertiser-activate-nav">
            <button type="button" className="advertiser-activate-nav-brand" onClick={onLogoClick}>
                <span className="advertiser-activate-nav-wordmark">INVERT</span>
            </button>
        </nav>
    );
}

export default function AdvertiserActivate() {
    const token = useSelector(getToken);
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [tosAccepted, setTosAccepted] = useState(false);
    const [accountMeta, setAccountMeta] = useState({ slotCost: null, categoryId: null });
    const [currentTosVersion, setCurrentTosVersion] = useState(null);
    const slotCostForAgreement = accountMeta.slotCost;
    const { doc: agreementDoc } = usePlatformAdvertisingAgreement(slotCostForAgreement);
    const tosVersion = currentTosVersion || agreementDoc?.version;
    const agreementLink = platformAgreementPath(slotCostForAgreement);
    const [form, setForm] = useState({
        fname: '',
        lname: '',
        companyName: '',
        email: '',
        phoneNumber: '',
        password: '',
        confirmPassword: '',
    });

    useEffect(() => {
        if (!token) {
            navigate('/advertiserLogin/', { replace: true });
            return;
        }

        getAdvertiserAccount(token)
            .then((response) => {
                const data = response.data;
                if (data.accountActivated) {
                    navigate('/advertiserDashboard/home', { replace: true });
                    return;
                }
                setForm({
                    fname: data.fname || '',
                    lname: data.lname || '',
                    companyName: data.companyName || '',
                    email: data.email || '',
                    phoneNumber: data.phoneNumber || '',
                    password: '',
                    confirmPassword: '',
                });
                setAccountMeta({
                    slotCost: data.slotCost,
                    categoryId: data.categoryId,
                });
                setCurrentTosVersion(data.currentTosVersion || null);
                setLoading(false);
            })
            .catch(() => {
                setError('Could not load your account. Please try logging in again.');
                setLoading(false);
            });
    }, [token, navigate]);

    function onChange(field, value) {
        setForm((prev) => ({ ...prev, [field]: value }));
    }

    function onSubmit(event) {
        event.preventDefault();
        setError('');
        if (!tosAccepted) {
            setError('You must accept the INVERT Advertising Agreement to continue.');
            return;
        }

        if (!tosVersion) {
            setError('Could not verify Advertising Agreement version. Please refresh and try again.');
            return;
        }

        setSubmitting(true);
        activateAdvertiserAccount(token, {
            tosAccepted: true,
            tosVersion,
            fname: form.fname,
            lname: form.lname,
            companyName: form.companyName,
            phoneNumber: form.phoneNumber || undefined,
            password: form.password,
            confirmPassword: form.confirmPassword,
        })
            .then((response) => {
                const fullName = `${response.data.fname || form.fname} ${response.data.lname || form.lname}`.trim();
                if (fullName) {
                    localStorage.setItem('name', fullName);
                }
                navigate('/advertiserDashboard/home', {
                    replace: true,
                    state: response.data.emailWarning
                        ? { emailWarning: response.data.emailWarning }
                        : undefined
                });
            })
            .catch((err) => {
                const msg =
                    err.response?.data?.error ||
                    err.response?.data?.message ||
                    'Activation failed. Please check your information and try again.';
                setError(msg);
                setSubmitting(false);
            });
    }

    if (loading) {
        return (
            <div className="advertiser-activate-page">
                <ActivateNav onLogoClick={() => navigate('/')} />
                <div className="advertiser-activate-main">
                    <div className="advertiser-activate-card">
                        <p className="advertiser-activate-loading">Loading your account…</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="advertiser-activate-page">
            <ActivateNav onLogoClick={() => navigate('/')} />
            <div className="advertiser-activate-main">
                <div className="advertiser-activate-card">
                    <span className="advertiser-activate-title">Activate your account</span>
                    <span className="advertiser-activate-subtitle">
                        Confirm your details and set a password to access the advertiser dashboard.
                    </span>

                    <form onSubmit={onSubmit}>
                        <section className="advertiser-activate-section">
                            <h2>Your information</h2>
                            <div className="advertiser-activate-field">
                                <label htmlFor="activate-fname">First name</label>
                                <input
                                    id="activate-fname"
                                    type="text"
                                    required
                                    value={form.fname}
                                    onChange={(e) => onChange('fname', e.target.value)}
                                />
                            </div>
                            <div className="advertiser-activate-field">
                                <label htmlFor="activate-lname">Last name</label>
                                <input
                                    id="activate-lname"
                                    type="text"
                                    required
                                    value={form.lname}
                                    onChange={(e) => onChange('lname', e.target.value)}
                                />
                            </div>
                            <div className="advertiser-activate-field">
                                <label htmlFor="activate-company">Company name</label>
                                <input
                                    id="activate-company"
                                    type="text"
                                    required
                                    value={form.companyName}
                                    onChange={(e) => onChange('companyName', e.target.value)}
                                />
                            </div>
                            <div className="advertiser-activate-field">
                                <label htmlFor="activate-email">Email</label>
                                <input id="activate-email" type="email" readOnly value={form.email} />
                            </div>
                            <div className="advertiser-activate-field">
                                <label htmlFor="activate-phone">Phone (optional)</label>
                                <input
                                    id="activate-phone"
                                    type="tel"
                                    value={form.phoneNumber}
                                    onChange={(e) => onChange('phoneNumber', e.target.value)}
                                    placeholder="(555) 000-0000"
                                />
                            </div>
                            {accountMeta.slotCost != null && (
                                <p className="advertiser-activate-readonly">
                                    Subscription slot cost (set by INVERT):{' '}
                                    {currencyFormatter.format(accountMeta.slotCost)}
                                </p>
                            )}
                        </section>

                        <section className="advertiser-activate-section">
                            <h2>Set your password</h2>
                            <div className="advertiser-activate-field">
                                <label htmlFor="activate-password">New password</label>
                                <input
                                    id="activate-password"
                                    type="password"
                                    required
                                    minLength={8}
                                    value={form.password}
                                    onChange={(e) => onChange('password', e.target.value)}
                                />
                            </div>
                            <div className="advertiser-activate-field">
                                <label htmlFor="activate-confirm">Confirm password</label>
                                <input
                                    id="activate-confirm"
                                    type="password"
                                    required
                                    minLength={8}
                                    value={form.confirmPassword}
                                    onChange={(e) => onChange('confirmPassword', e.target.value)}
                                />
                            </div>
                        </section>

                        <section className="advertiser-activate-section advertiser-activate-tos-section">
                            <h2>INVERT Advertising Agreement</h2>
                            <p className="advertiser-activate-agreement-note">
                                Network advertising across all participating INVERT screens. Review the agreement
                                using the link below before activating your account.
                            </p>
                            <label className="advertiser-activate-checkbox">
                                <input
                                    type="checkbox"
                                    checked={tosAccepted}
                                    onChange={(e) => {
                                    setTosAccepted(e.target.checked);
                                    if (e.target.checked) {
                                        setError('');
                                    }
                                }}
                                    required
                                />
                                <span>
                                    I agree to the{' '}
                                    <Link
                                        to={agreementLink}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="advertiser-activate-tos-link"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        INVERT Advertising Agreement
                                    </Link>{' '}
                                    {tosVersion ? `(version ${tosVersion})` : ''} for platform campaigns across the
                                    INVERT screen network.
                                </span>
                            </label>
                        </section>

                        {error && <p className="advertiser-activate-error">{error}</p>}

                        <button
                            type="submit"
                            className="advertiser-activate-submit"
                            disabled={submitting}
                        >
                            {submitting ? 'Activating…' : 'Activate account'}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
}
